import { useState } from 'react'
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material'
import {
  ExpandMore,
  ExpandLess,
  DirectionsWalk,
  Hiking,
  DirectionsBike,
  DirectionsCar,
  DirectionsBus,
  DirectionsBoat,
  Kayaking,
  Accessible,
  ElectricBike,
  Route as RouteIcon,
} from '@mui/icons-material'
import type { RouteTravelMode } from '@features/adventures/types/workflow.types'

interface RouteInfo {
  id: string
  label: string
  color: string
  travelMode: RouteTravelMode
  distanceMeters?: number
}

interface RouteLegendProps {
  routes: RouteInfo[]
  onRouteClick?: (routeId: string) => void
}

// Get icon component for travel mode
const getTravelModeIconComponent = (mode: RouteTravelMode) => {
  switch (mode) {
    case 'foot-walking':
      return DirectionsWalk
    case 'foot-hiking':
      return Hiking
    case 'cycling':
      return DirectionsBike
    case 'cycling-electric':
      return ElectricBike
    case 'driving':
      return DirectionsCar
    case 'public-transit':
      return DirectionsBus
    case 'ferry':
      return DirectionsBoat
    case 'water-kayak':
      return Kayaking
    case 'wheelchair':
      return Accessible
    default:
      return RouteIcon
  }
}

// Get label for travel mode
const getTravelModeLabel = (mode: RouteTravelMode): string => {
  switch (mode) {
    case 'foot-walking': return 'Walking'
    case 'foot-hiking': return 'Hiking'
    case 'cycling': return 'Cycling'
    case 'cycling-electric': return 'E-Bike'
    case 'driving': return 'Driving'
    case 'public-transit': return 'Public Transit'
    case 'ferry': return 'Ferry'
    case 'water-kayak': return 'Kayak/Paddle'
    case 'wheelchair': return 'Wheelchair'
    default: return 'Route'
  }
}

// Format distance for display
const formatDistance = (meters?: number): string => {
  if (!meters) return ''
  if (meters >= 1000) {
    return `${(meters / 1000).toFixed(1)} km`
  }
  return `${Math.round(meters)} m`
}

export function RouteLegend({ routes, onRouteClick }: RouteLegendProps) {
  const [expanded, setExpanded] = useState(true)

  if (routes.length === 0) {
    return null
  }

  // Calculate total distance
  const totalDistance = routes.reduce((sum, route) => sum + (route.distanceMeters || 0), 0)

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'absolute',
        top: 10,
        left: 10,
        zIndex: 1000,
        maxWidth: 280,
        maxHeight: 'calc(100% - 20px)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 1,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RouteIcon fontSize="small" />
          <Typography variant="subtitle2" fontWeight={600}>
            Routes ({routes.length})
          </Typography>
        </Box>
        <IconButton size="small" sx={{ color: 'inherit', p: 0 }}>
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>

      {/* Route List */}
      <Collapse in={expanded}>
        <List dense sx={{ py: 0, maxHeight: 300, overflow: 'auto' }}>
          {routes.map((route) => {
            const IconComponent = getTravelModeIconComponent(route.travelMode)
            const modeLabel = getTravelModeLabel(route.travelMode)
            const distance = formatDistance(route.distanceMeters)

            return (
              <ListItem
                key={route.id}
                sx={{
                  py: 0.5,
                  cursor: onRouteClick ? 'pointer' : 'default',
                  '&:hover': onRouteClick ? { bgcolor: 'action.hover' } : undefined,
                }}
                onClick={() => onRouteClick?.(route.id)}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Tooltip title={modeLabel}>
                    <IconComponent sx={{ color: route.color, fontSize: 20 }} />
                  </Tooltip>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 16,
                          height: 4,
                          bgcolor: route.color,
                          borderRadius: 1,
                          flexShrink: 0,
                        }}
                      />
                      <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                        {route.label}
                      </Typography>
                    </Box>
                  }
                  secondary={distance ? `${modeLabel} • ${distance}` : modeLabel}
                  primaryTypographyProps={{ component: 'div' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
            )
          })}
        </List>

        {/* Total Distance */}
        {totalDistance > 0 && (
          <Box
            sx={{
              px: 1.5,
              py: 1,
              borderTop: 1,
              borderColor: 'divider',
              bgcolor: 'grey.50',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Total: <strong>{formatDistance(totalDistance)}</strong>
            </Typography>
          </Box>
        )}
      </Collapse>
    </Paper>
  )
}
